import React, { useCallback, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";

import { DataTable } from "../../components/data-table";
import type { AdminOrderTransaction } from "../../lib/api/adminTransactionsData";
import { useAdminTransactionsDataQuery } from "../../lib/api/useAdminTransactionsDataQuery";
import AdminOrderDetailModal from "../orders/AdminOrderDetailModal";
import {
  buildOrderTableRows,
  createOrderTableColumns,
  type OrderTableRow,
} from "../orders/ordersOrderTable";

const RECENT_ORDERS_LIMIT = 10;

/** A trimmed version of the OrdersOverview table: 10 most recent orders. */
const RecentOrdersTable: React.FC = () => {
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTransaction, setDetailTransaction] =
    useState<AdminOrderTransaction | null>(null);

  const { data, isPending, isError, error, refetch } =
    useAdminTransactionsDataQuery({
      page: 1,
      orderSubtype: "sell",
      limit: RECENT_ORDERS_LIMIT,
    });

  const rows = useMemo(
    () =>
      buildOrderTableRows(data?.transactions ?? []).slice(
        0,
        RECENT_ORDERS_LIMIT
      ),
    [data?.transactions]
  );

  const openDetail = useCallback((row: OrderTableRow) => {
    setDetailTransaction(row.raw);
    setDetailOpen(true);
  }, []);

  const columns = useMemo(
    () => createOrderTableColumns(openDetail),
    [openDetail]
  );

  if (isPending) {
    return (
      <div className="flex justify-center py-16 rounded-xl border border-[#2C363E] bg-[#161E25]/40">
        <TailSpin color="#0B75C2" height={36} width={36} />
      </div>
    );
  }

  if (isError) {
    const message =
      error?.response?.data &&
      typeof error.response.data === "object" &&
      "message" in error.response.data
        ? String((error.response.data as { message?: string }).message)
        : "Could not load recent orders.";
    return (
      <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span>{message}</span>
        <button
          type="button"
          onClick={() => refetch()}
          className="shrink-0 rounded-lg bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 border border-red-500/40 hover:bg-red-500/30 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <DataTable<OrderTableRow>
        columns={columns}
        data={rows}
        onRowClick={openDetail}
        emptyContent="No recent orders."
        className="rounded-xl border border-[#2C363E] overflow-hidden"
        footerLeftContent={
          <span className="text-[12px] text-[#869AA9]">
            Showing {rows.length} most recent order
            {rows.length === 1 ? "" : "s"}
          </span>
        }
      />
      <AdminOrderDetailModal
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailTransaction(null);
        }}
        transaction={detailTransaction}
      />
    </>
  );
};

export default RecentOrdersTable;
